import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { api } from "$convex/convexApi";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { canManageInternalEvents } from "@/lib/roles";
import {
  CalendarDays,
  CheckCircle2,
  DollarSign,
  ImageIcon,
  Pencil,
  Plus,
  Ticket,
  Trash2,
  Upload,
  Users,
} from "lucide-react";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  EVENT_IMAGE_HELP_TEXT,
  EVENT_IMAGE_INPUT_ACCEPT,
  EVENT_IMAGE_TOO_LARGE_MESSAGE,
  prepareEventImageForUpload,
  validateEventImageFile,
} from "@/lib/event-image-upload";
import { exportToCSV } from "@/lib/export";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

type EventStatus = "draft" | "published" | "cancelled" | "completed";
type ParticipantStatus = "registered" | "confirmed" | "attended" | "cancelled" | "no_show";
type PaymentStatus = "unpaid" | "paid" | "refunded" | "comped";

type EventRecord = {
  _id: string;
  title: string;
  description?: string;
  location?: string;
  date?: string;
  time?: string;
  eventDateTime?: number;
  price?: number;
  totalSpots?: number;
  filledSpots?: number;
  spotsAvailable?: number;
  checkoutLink?: string;
  externalLink?: string;
  imageUrl?: string;
  removeDate?: number;
  featured?: boolean;
  status?: EventStatus;
  createdAt?: number;
  updatedAt?: number;
  participantSummary?: {
    registeredSeats: number;
    attendedSeats: number;
    noShowSeats: number;
    paidSeats: number;
    refundedSeats: number;
    revenue: number;
    participantRecords: number;
  };
};

type ParticipantRecord = {
  _id: string;
  eventId: string;
  eventTitle?: string;
  name: string;
  email?: string;
  company?: string;
  phone?: string;
  seats: number;
  status: ParticipantStatus;
  paymentStatus: PaymentStatus;
  amountPaid?: number;
  notes?: string;
  registeredAt?: number;
  checkedInAt?: number;
  createdAt?: number;
  updatedAt?: number;
};

type EventForm = {
  title: string;
  description: string;
  location: string;
  date: string;
  time: string;
  eventDateTimeLocal: string;
  price: string;
  isPaid: boolean;
  totalSpots: string;
  filledSpots: string;
  checkoutLink: string;
  externalLink: string;
  imageUrl: string;
  removeDateLocal: string;
  featured: boolean;
  status: EventStatus;
};

type ParticipantForm = {
  eventId: string;
  name: string;
  email: string;
  company: string;
  phone: string;
  seats: string;
  status: ParticipantStatus;
  paymentStatus: PaymentStatus;
  amountPaid: string;
  notes: string;
  registeredAtLocal: string;
  checkedInAtLocal: string;
};

const EVENT_STATUS_OPTIONS: { value: EventStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
];

const PARTICIPANT_STATUS_OPTIONS: { value: ParticipantStatus; label: string }[] = [
  { value: "registered", label: "Registered" },
  { value: "confirmed", label: "Confirmed" },
  { value: "attended", label: "Attended" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No show" },
];

const PAYMENT_STATUS_OPTIONS: { value: PaymentStatus; label: string }[] = [
  { value: "unpaid", label: "Unpaid" },
  { value: "paid", label: "Paid" },
  { value: "refunded", label: "Refunded" },
  { value: "comped", label: "Comped" },
];

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("en-US");

const initialEventForm: EventForm = {
  title: "",
  description: "",
  location: "",
  date: "",
  time: "",
  eventDateTimeLocal: "",
  price: "",
  isPaid: false,
  totalSpots: "",
  filledSpots: "0",
  checkoutLink: "",
  externalLink: "",
  imageUrl: "",
  removeDateLocal: "",
  featured: false,
  status: "draft",
};

const initialParticipantForm: ParticipantForm = {
  eventId: "",
  name: "",
  email: "",
  company: "",
  phone: "",
  seats: "1",
  status: "registered",
  paymentStatus: "unpaid",
  amountPaid: "",
  notes: "",
  registeredAtLocal: "",
  checkedInAtLocal: "",
};

function toLocalDateTimeInput(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  const date = new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromLocalDateTimeInput(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : undefined;
}

function optionalString(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function optionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDateTime(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "Not scheduled";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatCurrency(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "Free";
  if (value === 0) return "Free";
  return currencyFormatter.format(value);
}

function eventStatusBadgeClass(status: EventStatus | undefined) {
  switch (status) {
    case "published":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300";
    case "cancelled":
      return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300";
    case "completed":
      return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300";
    default:
      return "border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300";
  }
}

function participantStatusBadgeClass(status: ParticipantStatus) {
  switch (status) {
    case "attended":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300";
    case "cancelled":
      return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300";
    case "no_show":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300";
    default:
      return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300";
  }
}

function paymentStatusBadgeClass(status: PaymentStatus) {
  switch (status) {
    case "paid":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300";
    case "refunded":
      return "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900/60 dark:bg-purple-950/40 dark:text-purple-300";
    case "comped":
      return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-300";
    default:
      return "border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300";
  }
}

function eventToForm(event: EventRecord | null): EventForm {
  if (!event) return initialEventForm;
  const price = typeof event.price === "number" ? event.price : undefined;
  return {
    title: event.title ?? "",
    description: event.description ?? "",
    location: event.location ?? "",
    date: event.date ?? "",
    time: event.time ?? "",
    eventDateTimeLocal: toLocalDateTimeInput(event.eventDateTime),
    price: price === undefined ? "" : String(price),
    isPaid: Number(price ?? 0) > 0,
    totalSpots: event.totalSpots === undefined ? "" : String(event.totalSpots),
    filledSpots: event.filledSpots === undefined ? "0" : String(event.filledSpots),
    checkoutLink: event.checkoutLink ?? "",
    externalLink: event.externalLink ?? "",
    imageUrl: event.imageUrl ?? "",
    removeDateLocal: toLocalDateTimeInput(event.removeDate),
    featured: Boolean(event.featured),
    status: event.status ?? "draft",
  };
}

function participantToForm(participant: ParticipantRecord | null, defaultEventId: string): ParticipantForm {
  if (!participant) {
    return {
      ...initialParticipantForm,
      eventId: defaultEventId,
      registeredAtLocal: toLocalDateTimeInput(Date.now()),
    };
  }

  return {
    eventId: participant.eventId,
    name: participant.name ?? "",
    email: participant.email ?? "",
    company: participant.company ?? "",
    phone: participant.phone ?? "",
    seats: String(participant.seats ?? 1),
    status: participant.status ?? "registered",
    paymentStatus: participant.paymentStatus ?? "unpaid",
    amountPaid: participant.amountPaid === undefined ? "" : String(participant.amountPaid),
    notes: participant.notes ?? "",
    registeredAtLocal: toLocalDateTimeInput(participant.registeredAt),
    checkedInAtLocal: toLocalDateTimeInput(participant.checkedInAt),
  };
}

function buildEventPayload(form: EventForm) {
  const title = form.title.trim();
  if (!title) throw new Error("Event title is required.");

  const price = form.isPaid ? optionalNumber(form.price) : 0;
  if (form.isPaid && (!price || price <= 0)) {
    throw new Error("Paid events need a price greater than $0.");
  }

  const totalSpots = optionalNumber(form.totalSpots);
  const filledSpots = optionalNumber(form.filledSpots);
  if (totalSpots !== undefined && filledSpots !== undefined && filledSpots > totalSpots) {
    throw new Error("Filled seats cannot exceed total seats.");
  }

  return {
    title,
    description: optionalString(form.description),
    location: optionalString(form.location),
    date: optionalString(form.date),
    time: optionalString(form.time),
    eventDateTime: fromLocalDateTimeInput(form.eventDateTimeLocal),
    price,
    totalSpots,
    filledSpots,
    checkoutLink: optionalString(form.checkoutLink),
    externalLink: optionalString(form.externalLink),
    imageUrl: optionalString(form.imageUrl),
    removeDate: fromLocalDateTimeInput(form.removeDateLocal),
    featured: form.featured,
    status: form.status,
  };
}

function buildParticipantPayload(form: ParticipantForm) {
  const name = form.name.trim();
  if (!name) throw new Error("Participant name is required.");
  if (!form.eventId) throw new Error("Choose an event for this participant.");

  const seats = Number(form.seats);
  if (!Number.isFinite(seats) || seats <= 0) {
    throw new Error("Seats must be greater than 0.");
  }

  const amountPaid = optionalNumber(form.amountPaid);
  if (amountPaid !== undefined && amountPaid < 0) {
    throw new Error("Amount paid cannot be negative.");
  }

  return {
    eventId: form.eventId,
    name,
    email: optionalString(form.email),
    company: optionalString(form.company),
    phone: optionalString(form.phone),
    seats,
    status: form.status,
    paymentStatus: form.paymentStatus,
    amountPaid,
    notes: optionalString(form.notes),
    registeredAt: fromLocalDateTimeInput(form.registeredAtLocal),
    checkedInAt: form.status === "attended"
      ? (fromLocalDateTimeInput(form.checkedInAtLocal) ?? Date.now())
      : fromLocalDateTimeInput(form.checkedInAtLocal),
  };
}

function EventDialog({
  open,
  onOpenChange,
  event,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventRecord | null;
}) {
  const [form, setForm] = React.useState<EventForm>(() => eventToForm(event));
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const createEvent = useMutation(api.events.createEvent);
  const updateEvent = useMutation(api.events.updateEvent);
  const updateImage = useAction(api.events.updateEventImage);

  const imagePreviewUrl = React.useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile],
  );
  const displayedImageUrl = imagePreviewUrl ?? form.imageUrl;

  React.useEffect(() => {
    if (open) {
      setForm(eventToForm(event));
      setImageFile(null);
    }
  }, [event, open]);

  React.useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  const set = <K extends keyof EventForm>(key: K, value: EventForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleImageSelect = (file: File) => {
    const validationError = validateEventImageFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setImageFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageSelect(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildEventPayload(form);
      const savedEvent = event
        ? await updateEvent({ eventId: event._id, ...payload })
        : await createEvent(payload);

      if (imageFile && savedEvent?._id) {
        setUploading(true);
        try {
          const preparedImage = await prepareEventImageForUpload(imageFile);
          await updateImage({
            eventId: savedEvent._id,
            fileData: preparedImage.fileData,
            contentType: preparedImage.contentType,
            filename: preparedImage.filename,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : "Event saved, but image upload failed.";
          toast.error(message.includes("too large") ? EVENT_IMAGE_TOO_LARGE_MESSAGE : message);
          return;
        } finally {
          setUploading(false);
        }
      }

      toast.success(event ? "Event updated." : "Event created.");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>{event ? "Edit event" : "Create event"}</DialogTitle>
          <DialogDescription>
            Manage the public event listing, capacity, registration links, and hero image.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="mt-2">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="tickets">Tickets</TabsTrigger>
            <TabsTrigger value="image">Image</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 pt-4">
            <div className="grid gap-2">
              <Label htmlFor="event-title">Title *</Label>
              <Input
                id="event-title"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="Roaster Cupping Lab"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="event-description">Description</Label>
              <Textarea
                id="event-description"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Describe who should attend, what they will learn, and what is included."
                rows={4}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="event-location">Location</Label>
              <Input
                id="event-location"
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                placeholder="Anthem Coffee Imports HQ"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => set("status", value as EventStatus)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/30 p-3">
                <div>
                  <Label>Featured</Label>
                  <p className="text-xs text-muted-foreground">Feature this event at the top of the public events page.</p>
                </div>
                <Switch checked={form.featured} onCheckedChange={(value) => set("featured", value)} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="schedule" className="space-y-4 pt-4">
            <div className="grid gap-2">
              <Label htmlFor="event-datetime">Event date and time</Label>
              <Input
                id="event-datetime"
                type="datetime-local"
                value={form.eventDateTimeLocal}
                onChange={(e) => set("eventDateTimeLocal", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Used for sorting, past/upcoming sections, sitemap, and SEO rich results.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="event-date-label">Display date</Label>
                <Input
                  id="event-date-label"
                  value={form.date}
                  onChange={(e) => set("date", e.target.value)}
                  placeholder="June 12, 2026"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="event-time-label">Display time</Label>
                <Input
                  id="event-time-label"
                  value={form.time}
                  onChange={(e) => set("time", e.target.value)}
                  placeholder="10:00 AM – 2:00 PM"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="event-remove-date">Auto-remove date</Label>
              <Input
                id="event-remove-date"
                type="datetime-local"
                value={form.removeDateLocal}
                onChange={(e) => set("removeDateLocal", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Optional internal reference for when this event should be removed or completed.</p>
            </div>
          </TabsContent>

          <TabsContent value="tickets" className="space-y-4 pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/30 p-3">
                <div>
                  <Label>Paid event</Label>
                  <p className="text-xs text-muted-foreground">Track price and paid participant revenue.</p>
                </div>
                <Switch checked={form.isPaid} onCheckedChange={(value) => set("isPaid", value)} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="event-price">Price in dollars</Label>
                <Input
                  id="event-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  placeholder="25"
                  disabled={!form.isPaid}
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="event-total-spots">Total seats</Label>
                <Input
                  id="event-total-spots"
                  type="number"
                  min="0"
                  step="1"
                  value={form.totalSpots}
                  onChange={(e) => set("totalSpots", e.target.value)}
                  placeholder="20"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="event-filled-spots">Manual filled seats</Label>
                <Input
                  id="event-filled-spots"
                  type="number"
                  min="0"
                  step="1"
                  value={form.filledSpots}
                  onChange={(e) => set("filledSpots", e.target.value)}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">Useful for seats sold outside the participant roster.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="event-checkout-link">Checkout link</Label>
                <Input
                  id="event-checkout-link"
                  value={form.checkoutLink}
                  onChange={(e) => set("checkoutLink", e.target.value)}
                  placeholder="https://checkout.stripe.com/..."
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="event-external-link">External registration link</Label>
                <Input
                  id="event-external-link"
                  value={form.externalLink}
                  onChange={(e) => set("externalLink", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="image" className="space-y-4 pt-4">
            {displayedImageUrl ? (
              <div className="overflow-hidden rounded-2xl bg-muted shadow-sm ring-1 ring-black/10 dark:ring-white/10">
                <img
                  src={displayedImageUrl}
                  alt={form.title || "Event image"}
                  className="h-56 w-full object-cover"
                />
              </div>
            ) : (
              <div className="flex h-40 items-center justify-center rounded-2xl bg-muted text-muted-foreground ring-1 ring-border">
                <ImageIcon className="mr-2 h-5 w-5" /> No event image selected
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="event-image-url">Image URL</Label>
              <Input
                id="event-image-url"
                value={form.imageUrl}
                onChange={(e) => set("imageUrl", e.target.value)}
                placeholder="Uploaded R2 URL appears here, or paste an existing image URL"
              />
            </div>
            <label
              htmlFor="event-image-upload"
              className={cn(
                "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 transition-colors",
                dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/40",
              )}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">Drop an event image here</p>
              <p className="text-xs text-muted-foreground">or choose a file to upload to R2</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={saving || uploading}
              >
                {uploading ? "Uploading…" : "Choose file"}
              </Button>
              <input
                id="event-image-upload"
                ref={fileInputRef}
                type="file"
                accept={EVENT_IMAGE_INPUT_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageSelect(file);
                }}
              />
              <p className="text-xs text-muted-foreground">{EVENT_IMAGE_HELP_TEXT}</p>
            </label>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || uploading}>Cancel</Button>
          <Button onClick={() => void handleSave()} disabled={saving || uploading}>
            {saving || uploading ? "Saving…" : event ? "Save event" : "Create event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParticipantDialog({
  open,
  onOpenChange,
  participant,
  events,
  defaultEventId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  participant: ParticipantRecord | null;
  events: EventRecord[];
  defaultEventId: string;
}) {
  const [form, setForm] = React.useState<ParticipantForm>(() => participantToForm(participant, defaultEventId));
  const [saving, setSaving] = React.useState(false);
  const createParticipant = useMutation(api.events.createEventParticipant);
  const updateParticipant = useMutation(api.events.updateEventParticipant);

  React.useEffect(() => {
    if (open) {
      setForm(participantToForm(participant, defaultEventId));
    }
  }, [defaultEventId, participant, open]);

  const set = <K extends keyof ParticipantForm>(key: K, value: ParticipantForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const selectedEvent = events.find((event) => event._id === form.eventId);
  const suggestedAmount = selectedEvent?.price && Number(form.seats) > 0
    ? selectedEvent.price * Number(form.seats)
    : undefined;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildParticipantPayload(form);
      if (participant) {
        await updateParticipant({ participantId: participant._id, ...payload });
      } else {
        await createParticipant(payload);
      }
      toast.success(participant ? "Participant updated." : "Participant added.");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save participant.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>{participant ? "Edit participant" : "Add participant"}</DialogTitle>
          <DialogDescription>
            Track registration, attendance, seats, and paid-event revenue for an event.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Event *</Label>
            <Select value={form.eventId} onValueChange={(value) => set("eventId", value)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((event) => (
                  <SelectItem key={event._id} value={event._id}>{event.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="participant-name">Name *</Label>
              <Input id="participant-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Roaster" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="participant-email">Email</Label>
              <Input id="participant-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="participant-company">Company</Label>
              <Input id="participant-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Example Roasters" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="participant-phone">Phone</Label>
              <Input id="participant-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 555-5555" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="participant-seats">Seats *</Label>
              <Input id="participant-seats" type="number" min="1" step="1" value={form.seats} onChange={(e) => set("seats", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(value) => set("status", value as ParticipantStatus)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PARTICIPANT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Payment</Label>
              <Select value={form.paymentStatus} onValueChange={(value) => set("paymentStatus", value as PaymentStatus)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="participant-amount">Amount paid</Label>
              <Input id="participant-amount" type="number" min="0" step="0.01" value={form.amountPaid} onChange={(e) => set("amountPaid", e.target.value)} placeholder={suggestedAmount ? String(suggestedAmount) : "0"} />
              {suggestedAmount !== undefined && (
                <p className="text-xs text-muted-foreground">Suggested: {formatCurrency(suggestedAmount)}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="participant-registered">Registered at</Label>
              <Input id="participant-registered" type="datetime-local" value={form.registeredAtLocal} onChange={(e) => set("registeredAtLocal", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="participant-checked-in">Checked in at</Label>
              <Input id="participant-checked-in" type="datetime-local" value={form.checkedInAtLocal} onChange={(e) => set("checkedInAtLocal", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="participant-notes">Notes</Label>
            <Textarea id="participant-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Dietary notes, invoice reference, walk-in details, etc." rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={() => void handleSave()} disabled={saving || events.length === 0}>
            {saving ? "Saving…" : participant ? "Save participant" : "Add participant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useEventColumns() {
  return React.useMemo<ColumnDef<EventRecord>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Event" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {row.original.imageUrl ? (
              <img
                src={row.original.imageUrl}
                alt={row.original.title}
                className="h-12 w-16 rounded-xl object-cover shadow-sm ring-1 ring-black/10 dark:ring-white/10"
              />
            ) : (
              <div className="flex h-12 w-16 items-center justify-center rounded-xl bg-muted text-muted-foreground ring-1 ring-border">
                <ImageIcon className="h-4 w-4" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{row.original.title}</span>
                {row.original.featured && <Badge variant="outline" className="text-xs">Featured</Badge>}
              </div>
              <p className="max-w-[360px] truncate text-xs text-muted-foreground">{row.original.location || "No location set"}</p>
            </div>
          </div>
        ),
        meta: { label: "Event", placeholder: "Search events", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "eventDateTime",
        accessorKey: "eventDateTime",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Date" />,
        cell: ({ row }) => <span className="text-sm tabular-nums">{formatDateTime(row.original.eventDateTime)}</span>,
        meta: { label: "Date", variant: "date" },
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={eventStatusBadgeClass(row.original.status)}>
            {row.original.status ?? "draft"}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "select",
          options: EVENT_STATUS_OPTIONS.map((option) => ({ label: option.label, value: option.value })),
        },
        enableColumnFilter: true,
      },
      {
        id: "price",
        accessorKey: "price",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Price" />,
        cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.price)}</span>,
      },
      {
        id: "capacity",
        accessorFn: (row) => row.totalSpots ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Registered" />,
        cell: ({ row }) => {
          const total = row.original.totalSpots ?? 0;
          const filled = row.original.filledSpots ?? 0;
          const available = row.original.spotsAvailable ?? Math.max(0, total - filled);
          return (
            <div className="text-sm tabular-nums">
              <div>{filled}/{total || "∞"} registered</div>
              <div className="text-xs text-muted-foreground">{available} available</div>
            </div>
          );
        },
      },
      {
        id: "attendedSeats",
        accessorFn: (row) => row.participantSummary?.attendedSeats ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Attended" />,
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.participantSummary?.attendedSeats ?? 0}</span>
        ),
      },
      {
        id: "revenue",
        accessorFn: (row) => row.participantSummary?.revenue ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Revenue" />,
        cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.participantSummary?.revenue ?? 0)}</span>,
      },
    ],
    [],
  );
}

function useParticipantColumns() {
  return React.useMemo<ColumnDef<ParticipantRecord>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Participant" />,
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.name}</div>
            <div className="text-xs text-muted-foreground">{row.original.email || row.original.company || "No contact details"}</div>
          </div>
        ),
        meta: { label: "Participant", placeholder: "Search participants", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "eventTitle",
        accessorKey: "eventTitle",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Event" />,
        cell: ({ row }) => <span className="text-sm">{row.original.eventTitle || "Unknown event"}</span>,
        meta: { label: "Event", placeholder: "Search events", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "seats",
        accessorKey: "seats",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Seats" />,
        cell: ({ row }) => <span className="tabular-nums">{row.original.seats}</span>,
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={participantStatusBadgeClass(row.original.status)}>
            {row.original.status.replace("_", " ")}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "select",
          options: PARTICIPANT_STATUS_OPTIONS.map((option) => ({ label: option.label, value: option.value })),
        },
        enableColumnFilter: true,
      },
      {
        id: "paymentStatus",
        accessorKey: "paymentStatus",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Payment" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={paymentStatusBadgeClass(row.original.paymentStatus)}>
            {row.original.paymentStatus}
          </Badge>
        ),
        meta: {
          label: "Payment",
          variant: "select",
          options: PAYMENT_STATUS_OPTIONS.map((option) => ({ label: option.label, value: option.value })),
        },
        enableColumnFilter: true,
      },
      {
        id: "amountPaid",
        accessorKey: "amountPaid",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Paid" />,
        cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.amountPaid ?? 0)}</span>,
      },
      {
        id: "registeredAt",
        accessorKey: "registeredAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Registered" />,
        cell: ({ row }) => <span className="text-sm tabular-nums">{formatDateTime(row.original.registeredAt)}</span>,
      },
    ],
    [],
  );
}

function MetricCard({ icon: Icon, title, value, hint }: { icon: React.ElementType; title: string; value: string; hint: string }) {
  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tracking-tight tabular-nums">{value}</div>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border/70 bg-muted/20 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm text-foreground">{children}</div>
    </div>
  );
}

function EventDetailsDialog({
  open,
  onOpenChange,
  event,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventRecord | null;
}) {
  if (!event) return null;

  const summary = event.participantSummary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>{event.title}</DialogTitle>
          <DialogDescription>View-only event details and RSVP summary.</DialogDescription>
        </DialogHeader>

        {event.imageUrl && (
          <div className="overflow-hidden rounded-2xl bg-muted shadow-sm ring-1 ring-black/10 dark:ring-white/10">
            <img src={event.imageUrl} alt={event.title} className="h-56 w-full object-cover" />
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailRow label="Status">
            <Badge variant="outline" className={eventStatusBadgeClass(event.status)}>
              {event.status ?? "draft"}
            </Badge>
          </DetailRow>
          <DetailRow label="Date and time">{formatDateTime(event.eventDateTime)}</DetailRow>
          <DetailRow label="Display date">{event.date || "Not set"}</DetailRow>
          <DetailRow label="Display time">{event.time || "Not set"}</DetailRow>
          <DetailRow label="Location">{event.location || "Not set"}</DetailRow>
          <DetailRow label="Price">{formatCurrency(event.price)}</DetailRow>
          <DetailRow label="Registered seats">
            {numberFormatter.format(event.filledSpots ?? 0)}/{event.totalSpots ? numberFormatter.format(event.totalSpots) : "∞"}
          </DetailRow>
          <DetailRow label="Available seats">{numberFormatter.format(event.spotsAvailable ?? 0)}</DetailRow>
        </div>

        <DetailRow label="Description">
          <div className="whitespace-pre-wrap text-sm leading-relaxed">
            {event.description || "No description provided."}
          </div>
        </DetailRow>

        <div className="grid gap-3 sm:grid-cols-3">
          <DetailRow label="RSVP records">{numberFormatter.format(summary?.participantRecords ?? 0)}</DetailRow>
          <DetailRow label="Attended seats">{numberFormatter.format(summary?.attendedSeats ?? 0)}</DetailRow>
          <DetailRow label="Participant revenue">{formatCurrency(summary?.revenue ?? 0)}</DetailRow>
        </div>

        {(event.checkoutLink || event.externalLink) && (
          <div className="grid gap-3 sm:grid-cols-2">
            {event.checkoutLink && (
              <DetailRow label="Checkout link">
                <a className="break-all text-primary underline-offset-4 hover:underline" href={event.checkoutLink} target="_blank" rel="noopener noreferrer">
                  {event.checkoutLink}
                </a>
              </DetailRow>
            )}
            {event.externalLink && (
              <DetailRow label="External link">
                <a className="break-all text-primary underline-offset-4 hover:underline" href={event.externalLink} target="_blank" rel="noopener noreferrer">
                  {event.externalLink}
                </a>
              </DetailRow>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParticipantDetailsDialog({
  open,
  onOpenChange,
  participant,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  participant: ParticipantRecord | null;
}) {
  if (!participant) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>{participant.name}</DialogTitle>
          <DialogDescription>View-only RSVP and participant details.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailRow label="Event">{participant.eventTitle || "Unknown event"}</DetailRow>
          <DetailRow label="Seats">{numberFormatter.format(participant.seats)}</DetailRow>
          <DetailRow label="Status">
            <Badge variant="outline" className={participantStatusBadgeClass(participant.status)}>
              {participant.status.replace("_", " ")}
            </Badge>
          </DetailRow>
          <DetailRow label="Payment">
            <Badge variant="outline" className={paymentStatusBadgeClass(participant.paymentStatus)}>
              {participant.paymentStatus}
            </Badge>
          </DetailRow>
          <DetailRow label="Email">{participant.email || "Not provided"}</DetailRow>
          <DetailRow label="Company">{participant.company || "Not provided"}</DetailRow>
          <DetailRow label="Phone">{participant.phone || "Not provided"}</DetailRow>
          <DetailRow label="Amount paid">{formatCurrency(participant.amountPaid ?? 0)}</DetailRow>
          <DetailRow label="Registered at">{formatDateTime(participant.registeredAt)}</DetailRow>
          <DetailRow label="Checked in at">{formatDateTime(participant.checkedInAt)}</DetailRow>
        </div>

        <DetailRow label="Notes">
          <div className="whitespace-pre-wrap text-sm leading-relaxed">
            {participant.notes || "No notes provided."}
          </div>
        </DetailRow>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminEventsPage() {
  const { user, profile, userRole } = useUserProfileContext();
  const userEmail =
    (user as { email?: string } | undefined)?.email ??
    (profile as { email?: string } | undefined)?.email;
  const canManageEvents = canManageInternalEvents(userRole, userEmail);

  const eventsData = useQuery(api.events.listInternalEvents, {}) as EventRecord[] | undefined;
  const participantsData = useQuery(api.events.listEventParticipants, {}) as ParticipantRecord[] | undefined;
  const updateEventStatus = useMutation(api.events.updateEventStatus);
  const updateParticipant = useMutation(api.events.updateEventParticipant);
  const deleteParticipant = useMutation(api.events.deleteEventParticipant);

  const [activeTab, setActiveTab] = React.useState("events");
  const [createEventOpen, setCreateEventOpen] = React.useState(false);
  const [editEvent, setEditEvent] = React.useState<EventRecord | null>(null);
  const [viewEvent, setViewEvent] = React.useState<EventRecord | null>(null);
  const [participantOpen, setParticipantOpen] = React.useState(false);
  const [editParticipant, setEditParticipant] = React.useState<ParticipantRecord | null>(null);
  const [viewParticipant, setViewParticipant] = React.useState<ParticipantRecord | null>(null);
  const [participantEventId, setParticipantEventId] = React.useState("all");
  const [defaultParticipantEventId, setDefaultParticipantEventId] = React.useState("");

  const events = eventsData ?? [];
  const participants = participantsData ?? [];
  const eventColumns = useEventColumns();
  const participantColumns = useParticipantColumns();

  const filteredParticipants = React.useMemo(
    () => participantEventId === "all"
      ? participants
      : participants.filter((participant) => participant.eventId === participantEventId),
    [participantEventId, participants],
  );

  const metrics = React.useMemo(() => {
    const published = events.filter((event) => event.status === "published").length;
    const upcoming = events.filter((event) => typeof event.eventDateTime === "number" && event.eventDateTime >= Date.now()).length;
    const registeredSeats = events.reduce((sum, event) => sum + (event.participantSummary?.registeredSeats ?? 0), 0);
    const attendedSeats = events.reduce((sum, event) => sum + (event.participantSummary?.attendedSeats ?? 0), 0);
    const revenue = events.reduce((sum, event) => sum + (event.participantSummary?.revenue ?? 0), 0);
    return { published, upcoming, registeredSeats, attendedSeats, revenue };
  }, [events]);

  const openParticipantDialog = React.useCallback((eventId: string) => {
    setEditParticipant(null);
    setDefaultParticipantEventId(eventId);
    setParticipantOpen(true);
  }, []);

  const handleExportEvents = React.useCallback((selectedRows: EventRecord[]) => {
    exportToCSV(
      selectedRows.map((event) => ({
        title: event.title,
        status: event.status ?? "draft",
        date: formatDateTime(event.eventDateTime),
        location: event.location ?? "",
        price: event.price ?? 0,
        totalSeats: event.totalSpots ?? 0,
        filledSeats: event.filledSpots ?? 0,
        attendedSeats: event.participantSummary?.attendedSeats ?? 0,
        registeredSeats: event.participantSummary?.registeredSeats ?? 0,
        revenue: event.participantSummary?.revenue ?? 0,
      })),
      [
        { key: "title", label: "Title" },
        { key: "status", label: "Status" },
        { key: "date", label: "Date" },
        { key: "location", label: "Location" },
        { key: "price", label: "Price" },
        { key: "totalSeats", label: "Total seats" },
        { key: "filledSeats", label: "Filled seats" },
        { key: "registeredSeats", label: "Registered seats" },
        { key: "attendedSeats", label: "Attended seats" },
        { key: "revenue", label: "Revenue" },
      ],
      `events-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, []);

  const handleExportParticipants = React.useCallback((selectedRows: ParticipantRecord[]) => {
    exportToCSV(
      selectedRows.map((participant) => ({
        event: participant.eventTitle ?? "",
        name: participant.name,
        email: participant.email ?? "",
        company: participant.company ?? "",
        phone: participant.phone ?? "",
        seats: participant.seats,
        status: participant.status,
        paymentStatus: participant.paymentStatus,
        amountPaid: participant.amountPaid ?? 0,
        registeredAt: formatDateTime(participant.registeredAt),
        checkedInAt: formatDateTime(participant.checkedInAt),
        notes: participant.notes ?? "",
      })),
      [
        { key: "event", label: "Event" },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "company", label: "Company" },
        { key: "phone", label: "Phone" },
        { key: "seats", label: "Seats" },
        { key: "status", label: "Status" },
        { key: "paymentStatus", label: "Payment status" },
        { key: "amountPaid", label: "Amount paid" },
        { key: "registeredAt", label: "Registered at" },
        { key: "checkedInAt", label: "Checked in at" },
        { key: "notes", label: "Notes" },
      ],
      `event-participants-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, []);

  const handleBulkStatus = React.useCallback(async (selectedRows: EventRecord[], status: EventStatus, clearSelection: () => void) => {
    try {
      await Promise.all(selectedRows.map((event) => updateEventStatus({ eventId: event._id, status })));
      toast.success(`${selectedRows.length} event${selectedRows.length === 1 ? "" : "s"} updated.`);
      clearSelection();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update events.");
    }
  }, [updateEventStatus]);

  const handleBulkParticipantStatus = React.useCallback(async (selectedRows: ParticipantRecord[], status: ParticipantStatus, clearSelection: () => void) => {
    try {
      await Promise.all(selectedRows.map((participant) => updateParticipant({
        participantId: participant._id,
        eventId: participant.eventId,
        name: participant.name,
        email: participant.email,
        company: participant.company,
        phone: participant.phone,
        seats: participant.seats,
        status,
        paymentStatus: participant.paymentStatus,
        amountPaid: participant.amountPaid,
        notes: participant.notes,
        registeredAt: participant.registeredAt,
        checkedInAt: status === "attended" ? (participant.checkedInAt ?? Date.now()) : undefined,
      })));
      toast.success(`${selectedRows.length} participant${selectedRows.length === 1 ? "" : "s"} marked ${status.replace("_", " ")}.`);
      clearSelection();
    } catch (error) {
      toast.error(toUserFacingErrorMessage(error, "Couldn't update participants. Please try again."));
    }
  }, [updateParticipant]);

  const handleDeleteParticipants = React.useCallback(async (selectedRows: ParticipantRecord[], clearSelection: () => void) => {
    try {
      await Promise.all(selectedRows.map((participant) => deleteParticipant({ participantId: participant._id })));
      toast.success(`${selectedRows.length} participant${selectedRows.length === 1 ? "" : "s"} removed.`);
      clearSelection();
    } catch (error) {
      toast.error(toUserFacingErrorMessage(error, "Couldn't remove participants. Please try again."));
    }
  }, [deleteParticipant]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-balance">Events</h1>
          <p className="text-sm text-muted-foreground text-pretty">
            {canManageEvents
              ? "Create public events, upload R2 hero images, manage paid seats, and track registration/attendance."
              : "View event details, registration totals, and RSVP rosters."}
          </p>
        </div>
        {canManageEvents && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => { setActiveTab("participants"); openParticipantDialog(participantEventId === "all" ? (events[0]?._id ?? "") : participantEventId); }} disabled={events.length === 0}>
              <Users className="mr-1.5 h-4 w-4" /> Add participant
            </Button>
            <Button onClick={() => setCreateEventOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Create event
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard icon={CalendarDays} title="Published" value={numberFormatter.format(metrics.published)} hint={`${numberFormatter.format(metrics.upcoming)} upcoming`} />
        <MetricCard icon={Ticket} title="Registered seats" value={numberFormatter.format(metrics.registeredSeats)} hint="Excludes cancelled seats" />
        <MetricCard icon={CheckCircle2} title="Attended" value={numberFormatter.format(metrics.attendedSeats)} hint="Seats marked attended" />
        <MetricCard icon={DollarSign} title="Participant revenue" value={formatCurrency(metrics.revenue)} hint="Paid participant records" />
        <MetricCard icon={Users} title="Roster records" value={numberFormatter.format(participants.length)} hint="All participant rows" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Event management</CardTitle>
              <CardDescription>
                Use Events for the public listing and Participants for the roster, attendance, and payment details.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="events">Events</TabsTrigger>
              <TabsTrigger value="participants">Participants</TabsTrigger>
            </TabsList>

            <TabsContent value="events" className="space-y-4">
              {eventsData === undefined ? (
                <div className="space-y-3">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <InternalEntityDataTable
                  data={events}
                  columns={eventColumns}
                  pageSize={10}
                  initialSorting={[{ id: "eventDateTime", desc: true }]}
                  actionBar={({ selectedRows, clearSelection }) => (
                    <DataTableFloatingBar selectedCount={selectedRows.length} onClear={clearSelection}>
                      {selectedRows.length === 1 && (
                        canManageEvents ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setEditEvent(selectedRows[0] ?? null); clearSelection(); }}>
                              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { openParticipantDialog(selectedRows[0]?._id ?? ""); clearSelection(); setActiveTab("participants"); }}>
                              <Users className="mr-1.5 h-3.5 w-3.5" /> Add participant
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setViewEvent(selectedRows[0] ?? null); clearSelection(); }}>
                            View details
                          </Button>
                        )
                      )}
                      {canManageEvents && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => void handleBulkStatus(selectedRows, "published", clearSelection)}>Publish</Button>
                          <Button size="sm" variant="outline" onClick={() => void handleBulkStatus(selectedRows, "draft", clearSelection)}>Draft</Button>
                        </>
                      )}
                      {canManageEvents && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleExportEvents(selectedRows)}>Export</Button>
                          <Button size="sm" variant="destructive" onClick={() => void handleBulkStatus(selectedRows, "cancelled", clearSelection)}>Cancel</Button>
                        </>
                      )}
                    </DataTableFloatingBar>
                  )}
                />
              )}
            </TabsContent>

            <TabsContent value="participants" className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="participant-event-filter" className="text-sm">Event</Label>
                  <Select value={participantEventId} onValueChange={setParticipantEventId}>
                    <SelectTrigger id="participant-event-filter" className="w-[260px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All events</SelectItem>
                      {events.map((event) => (
                        <SelectItem key={event._id} value={event._id}>{event.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {canManageEvents && (
                  <Button size="sm" onClick={() => openParticipantDialog(participantEventId === "all" ? (events[0]?._id ?? "") : participantEventId)} disabled={events.length === 0}>
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Add participant
                  </Button>
                )}
              </div>

              {participantsData === undefined ? (
                <div className="space-y-3">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <InternalEntityDataTable
                  data={filteredParticipants}
                  columns={participantColumns}
                  pageSize={10}
                  initialSorting={[{ id: "registeredAt", desc: true }]}
                  actionBar={({ selectedRows, clearSelection }) => (
                    <DataTableFloatingBar selectedCount={selectedRows.length} onClear={clearSelection}>
                      {selectedRows.length === 1 && (
                        canManageEvents ? (
                          <Button size="sm" variant="outline" onClick={() => { setEditParticipant(selectedRows[0] ?? null); setParticipantOpen(true); clearSelection(); }}>
                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setViewParticipant(selectedRows[0] ?? null); clearSelection(); }}>
                            View details
                          </Button>
                        )
                      )}
                      {canManageEvents && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => void handleBulkParticipantStatus(selectedRows, "attended", clearSelection)}>
                            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Attended
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => void handleBulkParticipantStatus(selectedRows, "no_show", clearSelection)}>No show</Button>
                          <Button size="sm" variant="outline" onClick={() => void handleBulkParticipantStatus(selectedRows, "cancelled", clearSelection)}>Cancel RSVP</Button>
                        </>
                      )}
                      {canManageEvents && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleExportParticipants(selectedRows)}>Export</Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Remove {selectedRows.length} participant{selectedRows.length === 1 ? "" : "s"}?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  This removes the selected RSVPs from the event roster. Paid participants will not be automatically refunded. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  variant="destructive"
                                  onClick={() => void handleDeleteParticipants(selectedRows, clearSelection)}
                                >
                                  Remove participants
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </DataTableFloatingBar>
                  )}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {canManageEvents && (
        <>
          <EventDialog open={createEventOpen} onOpenChange={setCreateEventOpen} event={null} />
          <EventDialog open={!!editEvent} onOpenChange={(open) => { if (!open) setEditEvent(null); }} event={editEvent} />
          <ParticipantDialog
            open={participantOpen}
            onOpenChange={(open) => { if (!open) { setParticipantOpen(false); setEditParticipant(null); } }}
            participant={editParticipant}
            events={events}
            defaultEventId={defaultParticipantEventId || events[0]?._id || ""}
          />
        </>
      )}
      <EventDetailsDialog open={!!viewEvent} onOpenChange={(open) => { if (!open) setViewEvent(null); }} event={viewEvent} />
      <ParticipantDetailsDialog open={!!viewParticipant} onOpenChange={(open) => { if (!open) setViewParticipant(null); }} participant={viewParticipant} />
    </div>
  );
}
